import { axiosInstance } from "@/lib/axios-instance";

export interface PortcoUpdateFormData {
  update_title: string;
  update_description: string;
  portco_update?: Record<string, string>;
  files?: File[];
  file_types?: string[];
  drive_files?: Array<{
    _id: string;
    file_type: string;
    file_url?: string;
    file_name?: string;
  }>;
}

export interface PortcoUpdateResponse {
  status: string;
  message?: string;
  result?: {
    _id: string;
    update_title: string;
    files_count: number;
    status: string;
    created_on: string;
  };
}

export interface CompanySnapshot {
  company_name: string;
  company_website?: string;
  short_bio?: string;
  address?: string;
  country?: string;
  industry?: string[];
  business_model?: string;
  stage?: number;
  contact_phone?: string;
  contact_email?: string;
}

export interface PortcoUpdate {
  _id: string;
  opportunity_application?: string; // Optional since we now have direct company_id and opportunity_id
  company_id: string;
  opportunity_id: string;
  submitted_by: string;
  update_title: string;
  update_description: string;
  company_snapshot: CompanySnapshot;
  portco_update?: Record<string, string>;
  files?: Array<{
    file_name: string;
    file_type: string;
    file_url: string;
    file_size: string;
    uploaded_on: string;
    upload_source?: "local" | "google_drive" | "docsend";
  }>;
  status: "pending" | "approved" | "declined" | "reviewed";
  review_notes?: string;
  reviewed_by?: string;
  reviewed_on?: string;
  created_on: string;
  updated_on: string;
}

export interface GetPortcoUpdatesResponse {
  status: string;
  result?: PortcoUpdate[];
}

export interface GetPortcoUpdateResponse {
  status: string;
  result?: PortcoUpdate;
}

export const portcoUpdatesApi = {
  // Submit portfolio company update with file uploads
  submitUpdate: async (
    opportunityId: string,
    companyId: string,
    formData: PortcoUpdateFormData
  ): Promise<PortcoUpdateResponse> => {
    const data = new FormData();

    // Add text fields
    data.append("update_title", formData.update_title);
    data.append("update_description", formData.update_description);

    if (formData.portco_update) {
      data.append("portco_update", JSON.stringify(formData.portco_update));
    }

    // Add local files and their types
    if (formData.files && formData.files.length > 0) {
      formData.files.forEach((file) => {
        data.append("files", file);
      });

      if (formData.file_types) {
        data.append("file_types", JSON.stringify(formData.file_types));
      }
    }

    // Add Google Drive files
    if (formData.drive_files && formData.drive_files.length > 0) {
      data.append("drive_files", JSON.stringify(formData.drive_files));
    }

    const response = await axiosInstance.post(
      `/submit_portco_update?opportunity_id=${opportunityId}&company_id=${companyId}`,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },

  // Get all portco updates for an opportunity application
  getUpdates: async (
    opportunityId: string,
    companyId: string
  ): Promise<GetPortcoUpdatesResponse> => {
    const response = await axiosInstance.get(
      `/get_portco_updates?opportunity_id=${opportunityId}&company_id=${companyId}`
    );
    return response.data;
  },

  // Get specific portco update
  getUpdate: async (updateId: string): Promise<GetPortcoUpdateResponse> => {
    const response = await axiosInstance.get(`/get_portco_update/${updateId}`);
    return response.data;
  },
};
